<template>
  <div
    id="map"
    :class="['management-workspace-map', 'elevation-1', { 'npv-class':  isNpv}]"
    type="light"
    oncontextmenu="return false"
  >
    <change-map-type-button
      @click="changeViewType"
      :viewIsAerial="isAerial"
      v-if="!drawPolygon"
      style="margin: 85px 0 0 10px"
    />
    <template v-if="drawPolygon">
      <div class="map-buttons">
        <v-btn
          @click="dialog = true"
          small
          color="accent"
          class="ma-0 mb-2"
          style="width: 120px"
        >
          <v-icon left>upload</v-icon>
          <span style="text-transform: none">{{ $t("UploadFile") }}</span>
        </v-btn>

        <v-btn
          @click="resetPolygon"
          :disabled="!featureIsDrawn"
          small
          color="accent"
          class="ma-0 mb-2"
          style="width: 120px"
          :class="{ aerial: isAerial }"
        >
          <v-icon left>replay</v-icon>
          <span style="text-transform: none">{{ $t("StartOver") }}</span>
        </v-btn>

        <v-btn
          @click="showCoordinatesDialog"
          small
          color="accent"
          class="ma-0"
          style="width: 120px"
        >
          <v-icon class="ml-1" left>place</v-icon>
          <span style="text-transform: none">{{ $tc("Coordinate", 2) }}</span>
        </v-btn>
      </div>

      <v-btn
        v-if="viewAllWorkspaces"
        @click="toggleAllWorkspaces"
        :loading="allWorkspacesLoading"
        small
        color="accent"
        class="ma-0 all-workspaces-button"
      >
        <v-checkbox
          :input-value="showAllWorkspaces"
          left
          style="padding-top: 10px;"
          color="white"
        />

        <span style="text-transform: none">{{ $t("ViewAllWorkspaces") }}</span>
      </v-btn>

      <center-workspace-button
        :disabled="!workspaceFeature"
        @click="centerOnWorkspace"
        :viewIsAerial="isAerial"
      />

      <change-map-type-button
        @click="changeViewType"
        :viewIsAerial="isAerial"
      />

      <v-dialog v-model="dialog" width="400" persistent>
        <v-card class="delete-modal">
          <v-card-title class="headline">{{ $t("UploadFile") }}</v-card-title>

          <v-card-text>
            <v-layout column>
              <div class="input-field-container" style="margin-bottom: 10px;">
                <label>{{ $tc("Type", 1) }}</label>
                <treeselect
                  v-model="typeSelect"
                  :options="typeSelectOptions"
                  :placeholder="null"
                  :searchable="false"
                />
              </div>
              <div class="input-field-container">
                <label>{{ $tc("Projection", 1) }}</label>
                <treeselect
                  v-model="projection"
                  :options="projectionOptions"
                  :load-options="loadProjections"
                  :placeholder="null"
                  disableFuzzyMatching
                />
              </div>

              <drop-file-area
                style="padding-top: 20px"
                ref="dropFileArea"
                :file-types="acceptedFileTypes"
                :disabled="!fileSelectionEnabled"
                @updateFile="dropzoneFile = $event"
                @trashFile="dropzoneFile = null"
              />
            </v-layout>
          </v-card-text>

          <v-card-actions style="padding: 8px">
            <v-spacer />

            <management-button
              type="cancel"
              :label="$t('Cancel')"
              @buttonClick="cancelForm"
            />
            <v-btn
              small
              color="accent"
              class="text-capitalize"
              style="font-size:12px; padding: 16px 29px;border-radius: 4px;"
              @click="submitForm"
              :disabled="!buttonActive"
              :loading="loading"
            >
              {{ $t("Submit") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <workspace-coordinates
        :coordinatesDialog="coordinatesDialog"
        :coordinates="coordinates"
        @close-coordinates-dialog="closeCoordinatesDialog"
        @center-on-coordinates="centerOnCoordinates"
      />
    </template>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { managementMapsMixin } from "@/mixins/management/managementMapsMixin";
import { GeoJSON, WKT } from "ol/format";

import vueDropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import ManagementButton from "@/components/Management/ManagementButton";
import { Draw, Modify, Snap } from "ol/interaction";
import CenterWorkspaceButton from "@/components/_Atoms/CenterLocationButton";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import { Vector as VectorSource } from "ol/source";
import { Vector as VectorLayer } from "ol/layer";
import utils from "@/utils";
import DropFileArea from "@/components/_Molecules/DropFileArea";
import ChangeMapTypeButton from "@/components/_Atoms/ChangeMapTypeButton";
import { Circle, Icon, Fill, Stroke, Style } from "ol/style";
import { Feature } from "ol";
import { transform } from "ol/proj";
import { Point } from "ol/geom.js";
import WorkspaceCoordinates from "./WorkspaceCoordinates";
import defaultBuildingImage from "@/assets/npv/default_building.png";
import selectedBuildingImage from "@/assets/npv/selected_building.png";

export default {
  name: "WorkspaceMap",

  components: {
    DropFileArea,
    CenterWorkspaceButton,
    ManagementButton,
    vueDropzone,
    ChangeMapTypeButton,
    WorkspaceCoordinates
  },

  mixins: [managementMapsMixin, checkPermissionsMixin],

  props: {
    workspace: {
      type: Object
    },

    drawPolygon: {
      type: Boolean
    },
    viewAllWorkspaces: {
      type: Boolean,
      default: true
    },
    isNpv:{
      type: Boolean,
      default: false
    },
    isNpvPreview:{
      type: Boolean,
      default: false
    },
    isNpvBuildings:{
      type: Boolean,
      default: false
    },
    polygon: {
      type: String
    },
    buildings: {
      type: Array,
      default: function(){
        return []
      }
    }
  },

  data() {
    return {
      draw: undefined,
      featureIsDrawn: false,

      modify: undefined,
      snap: undefined,
      activeType: null,

      snapSource: null,

      dialog: false,
      loading: false,

      typeSelect: null,
      projection: null,
      dropzoneFile: null,
      featureWKT: null,

      typeSelectOptions: [
        { id: "shape", label: "Shape" },
        { id: "kml", label: "KML" }
      ],

      projectionOptions: null,

      workspaceFeature: null,

      showAllWorkspaces: false,
      listOfWorkspaces: undefined,
      allWorkspacesLoading: false,
      allWorkspacesStyle: null,
      allWorkspacesSource: null,
      allWorkspacesLayer: null,

      coordinatesDialog: false,
      coordinates: {
        latitude: null,
        longitude: null
      },

      markPointFeature: null,
      pointLayer: null
    };
  },

  computed: {
    buttonActive() {
      return this.typeSelect && this.projection && this.dropzoneFile;
    },

    acceptedFileTypes() {
      return [".zip", ".rar", ".kml"];
    },

    /**
     * Determines if the drop file area should be enabled ot not.
     */
    fileSelectionEnabled() {
      return this.typeSelect !== undefined && this.projection !== undefined;
    },
    /**
     * return NPV selected buildings.
     */
    selectedBuildings() {
      return this.$store.getters.getSelectedBuildings;
    },
  },

  watch:{
    buildings(){
      this.createBuildingsLayer();
    },
    selectedBuildings(newValue){
      this.updateMarkerStyles(newValue);
    },
    polygon(newPolygonGeoJSON){
      this.showWorkspace({
        wp_geo_json: newPolygonGeoJSON
      }, true)
    },
    workspace(){
      this.showWorkspace(this.workspace);
    },
    featureIsDrawn(value){
      if(this.isNpv && this.workspace){
        this.resetPolygon();
        this.$emit('clear-selected-workspace', value)
      }
    }
  },
  mounted() {
    this.createDefaultStyle();
    this.createMap();
    this.initializeMarkPoint();
    this.createAerialMapLayer();
    this.createPointLayer();
    this.setAllWorkspacesLayer();
    this.allWorkspacesLayer.setZIndex(3);
    this.vectorLayer.setZIndex(4);
    this.pointLayer.setZIndex(5);

    if (this.drawPolygon) {
      this.createDrawInteraction();
      this.createModifyInteraction();
      this.createSnapInteraction();

      if (this.workspace && this.workspace.text_geom) {
        this.featureIsDrawn = true;

        this.modify.setActive(true);
      } else {
        this.draw.setActive(true);
      }
    }

    if (this.workspace && (this.workspace.text_geom || this.workspace.wp_text_geom)) {
      this.showWorkspace(this.workspace);
    }
    if(this.polygon){
      this.showWorkspace({
        wp_geo_json: this.polygon
      }, true)
    }
    if(this.isNpvPreview){
      this.createBuildingsLayer();
    }
  },

  methods: {
    ...mapActions([
      "GET_LIST",
      "CREATE_IMPORT_VALIDATION",
      "fetchAllWorkspaces",
      "clearWorkspace",
      "clearWorkspaceGeometry"
    ]),

    createDrawInteraction() {
      this.draw = new Draw({
        source: this.vectorSource,
        type: "Polygon",
        style: this.defaultStyle
      });

      this.draw.on("drawstart", () => {
        this.featureIsDrawn = true;
      });

      this.draw.on("drawend", event => {
        this.updateWorkspaceFeature(event.feature);
        this.$nextTick(() => {
          this.centerOnWorkspace();
          this.snapSource.addFeature(event.feature);
        });
        this.draw.setActive(false);
        this.modify.setActive(true);
      });
      this.map.addInteraction(this.draw);
      this.draw.setActive(false);
    },

    createModifyInteraction() {
      this.modify = new Modify({ source: this.vectorSource });
      this.modify.on("modifyend", event => {
        this.updateWorkspaceFeature(event.features.item(0));
      });
      this.map.addInteraction(this.modify);
      this.modify.setActive(false);
    },

    createSnapInteraction() {
      this.snapSource = new VectorSource({ wrapX: false });
      this.snap = new Snap({
        source: this.snapSource
      });
      this.map.addInteraction(this.snap);
    },

    getProjection() {
      const mapProjection = this.map.getView().getProjection();
      return mapProjection.getCode();
    },

    resetPolygon() {
      if(this.isNpv && this.$store.getters.getWorkspace){
        this.clearWorkspace();
        this.clearWorkspaceGeometry();
      }
      this.modify.setActive(false);

      //it's necessary to delete the polygon in the middle of the drawing
      this.draw.finishDrawing();

      if (this.workspaceFeature && !this.isNpv) {
        this.snapSource.removeFeature(this.workspaceFeature);
      }
      this.workspaceFeature = null;
      this.vectorLayer.getSource().clear();

      this.draw.setActive(true);
      this.featureIsDrawn = false;
      this.$emit("feature-wkt", null);
    },

    loadProjections({ callback }) {
      let projectionOptions = [];
      this.GET_LIST({ listName: "list_projections", code: this.projection })
        .then(projections => {
          projections.forEach(projection => {
            projectionOptions.push({
              id: projection.code,
              label: projection.code + " : " + projection.definition
            });
          });
          this.projectionOptions = projectionOptions;
          callback();
        })
        .catch(error => {
          let errorHandle = utils.errorHandler(error);
          if (errorHandle && typeof errorHandle === "string") {
            this.$toasted.global.toast_error(errorHandle);
          }
        });
    },

    cancelForm() {
      this.typeSelect = null;
      this.projection = null;
      this.dropzoneFile = null;
      this.$refs.dropFileArea.trashFile();
      this.dialog = false;
    },

    submitForm() {
      this.loading = true;
      this.vectorLayer.getSource().clear();

      let file = this.dropzoneFile;
      if (!file) {
        return;
      }

      this.CREATE_IMPORT_VALIDATION({
        endpoint: "workspaces",
        data: this.getFormData(file)
      })
        .then(data => {
          this.addWorkspaceFeature(data[0].wp_geo_json);
          this.updateWorkspaceFeature(this.workspaceFeature);
          this.centerOnWorkspace();
          this.featureIsDrawn = true;
          this.modify.setActive(true);
          this.draw.setActive(false);

          this.loading = false;

          this.cancelForm();
        })
        .catch(error => {
          this.$toasted.global.toast_error(error.response.data.message);
          this.loading = false;
        });
    },

    getFormData(file) {
      let formData = new FormData();
      formData.append("file", file);
      formData.append("projection", this.projection);
      formData.append("file_type", this.typeSelect);
      formData.append("get_geom", true);
      return formData;
    },

    showWorkspace(workspace, isPolygon = false) {
      let geom;
      if (workspace && workspace.hasOwnProperty("text_geom")) {
        geom = workspace.text_geom;
      } else if (workspace && workspace.hasOwnProperty("wp_geo_json")) {
        geom = workspace.wp_geo_json;
      }
      if(geom){
        this.addWorkspaceFeature(geom, isPolygon);
      }
      this.centerOnWorkspace();
    },

    addWorkspaceFeature(workspaceGeoJSON, isPolygon) {
      this.getWorkspaceFeature(workspaceGeoJSON, isPolygon);
      if(this.isNpvBuildings){
        const newStyles = this.getNewPolygonStyles();
        this.workspaceFeature.setStyle(newStyles);
      }
      this.vectorSource.clear();
      this.vectorSource.addFeature(this.workspaceFeature);
      if (this.drawPolygon) {
        this.snapSource.addFeature(this.workspaceFeature);
      }
    },
    getNewPolygonStyles(){
      let defaultStroke = new Stroke({
        color: "#767676",
        width: 1.25
      });

      let defaultFill = new Fill({
        color: "rgba(230,240,245,0.5)"
      });

      const defaultStyle = new Style({
        image: new Circle({
          fill: defaultFill,
          stroke: defaultStroke,
          radius: 5
        }),
        fill: defaultFill,
        stroke: defaultStroke
      });

      return defaultStyle;
    },
    getWorkspaceFeature(workspaceGeoJSON, isPolygon) {
      if (workspaceGeoJSON) {
        let features = new GeoJSON().readFeatures(workspaceGeoJSON, {
          featureProjection: isPolygon ? "EPSG:4326" : "EPSG:3857",
        });
        this.workspaceFeature = features[0];
      }
    },

    centerOnWorkspace() {
      if(this.workspaceFeature){
        let workspaceExtent = this.workspaceFeature.getGeometry();
        this.map.getView().fit(workspaceExtent, this.map.getSize());
      }
    },

    updateWorkspaceFeature(feature) {
      this.workspaceFeature = feature;
      let geoJsonFormatter = new GeoJSON();
      let featureGeoJSON = geoJsonFormatter.writeFeature(feature);
      this.featureWKT = new WKT().writeFeature(
        geoJsonFormatter.readFeature(featureGeoJSON)
      );
      this.$emit("feature-wkt", this.featureWKT);
    },

    ///////////////////
    /*All Workspaces*/
    //////////////////

    setAllWorkspacesLayer() {
      this.createStyleForAllWorkspaces();
      this.createVectorLayerAllWorkspace();
      this.map.addLayer(this.allWorkspacesLayer);
    },

    createStyleForAllWorkspaces() {
      let defaultStroke = new Stroke({
        color: "#979797",
        width: 1.25
      });

      let defaultFill = new Fill({
        color: "rgba(219,219,220,0.4)"
      });

      this.allWorkspacesStyle = new Style({
        image: new Circle({
          fill: defaultFill,
          stroke: defaultStroke,
          radius: 5
        }),
        fill: defaultFill,
        stroke: defaultStroke
      });
    },

    createVectorLayerAllWorkspace() {
      this.allWorkspacesSource = new VectorSource({ wrapX: false });
      this.allWorkspacesLayer = new VectorLayer({
        source: this.allWorkspacesSource,
        style: this.allWorkspacesStyle
      });
    },

    toggleAllWorkspaces() {
      this.showAllWorkspaces = !this.showAllWorkspaces;
      if (this.showAllWorkspaces) {
        if (!this.listOfWorkspaces) {
          this.loadWorkspaces();
        } else {
          this.allWorkspacesLayer.setVisible(true);
        }
      } else {
        this.allWorkspacesLayer.setVisible(false);
      }
    },

    loadWorkspaces() {
      this.allWorkspacesLoading = true;
      this.fetchAllWorkspaces({
        includes: null,
        fields: "wp_text_geom,wp_geo_json,wp_id"
      })
        .then(workspaces => {
          this.listOfWorkspaces = workspaces;
          this.addAllWorkspacesFeatures(this.listOfWorkspaces);

          this.$nextTick(() => {
            this.allWorkspacesLayer.setVisible(true);
          });
        })
        .catch(error => {
          let errorHandle = utils.errorHandler(error);
          if (errorHandle && typeof errorHandle === "string") {
            this.$toasted.global.toast_error(errorHandle);
          }
        })
        .then(() => {
          this.allWorkspacesLoading = false;
        });
    },

    addAllWorkspacesFeatures(workspaces) {
      let geom;
      if (workspaces) {
        for (const workspace of workspaces) {
          if (
            workspace.hasOwnProperty("wp_geo_json") &&
            workspace.wp_id !== this.workspace.id
          ) {
            geom = workspace.wp_geo_json;
            this.addWorkspaceFeatureToAllWorkspaces(geom);
          }
        }
      }
    },

    addWorkspaceFeatureToAllWorkspaces(workspaceGeoJSON) {
      let features = new GeoJSON().readFeatures(workspaceGeoJSON, {
        featureProjection: "EPSG:3857"
      });
      let workspaceFeature = features[0];
      this.snapSource.addFeature(workspaceFeature);
      this.allWorkspacesSource.addFeature(workspaceFeature);
    },

    /////////////////////
    /*Coordinates Point*/
    /////////////////////

    showCoordinatesDialog() {
      this.coordinatesDialog = true;
    },

    closeCoordinatesDialog() {
      this.coordinatesDialog = false;
    },

    centerOnCoordinates() {
      this.pointlayerSource.clear();
      let feature = this.markPointFeature.clone();
      feature
        .getGeometry()
        .setCoordinates(
          transform(
            [
              parseFloat(this.coordinates.longitude),
              parseFloat(this.coordinates.latitude)
            ],
            "EPSG:4326",
            "EPSG:3857"
          )
        );
      this.pointlayerSource.addFeature(feature);

      this.mapView.setCenter(
        transform(
          [
            parseFloat(this.coordinates.longitude),
            parseFloat(this.coordinates.latitude)
          ],
          "EPSG:4326",
          "EPSG:3857"
        )
      );

      let point = feature.getGeometry();
      this.mapView.fit(point, {
        minResolution: 0.6
      });

      this.closeCoordinatesDialog();
      this.coordinates = {
        latitude: null,
        longitude: null
      };
    },

    initializeMarkPoint() {
      let iconFeature = new Feature({
        geometry: new Point([0, 0])
      });

      let iconStyle = new Style({
        image: new Icon({
          anchor: [20, 40],
          anchorXUnits: "pixels",
          anchorYUnits: "pixels",
          opacity: 1,
          src: require("@/assets/map_icons/location.png")
        }),
        zIndex: 2
      });

      iconFeature.setStyle(iconStyle);
      this.markPointFeature = iconFeature;
    },

    createPointLayer() {
      this.pointlayerSource = new VectorSource({ wrapX: false });
      this.pointLayer = new VectorLayer({
        source: this.pointlayerSource
      });
      this.map.addLayer(this.pointLayer);
    },
    createBuildingsLayer(){
      if(this.buildings.length > 0){
        this.addObjectsToMap(this.buildings);
      }
    },
    createCustomMarkerStyle(isSelected){
      const src = isSelected ? selectedBuildingImage : defaultBuildingImage;
      return new Style({
        image: new Icon({
          src,
          anchor: [0.5, 1], // Anchor point of the image
          scale: 1 // Scale factor for the image
        })
      });
    },
    addObjectsToMap(objectsArray) {
      const vectorSource = new VectorSource({ wrapX: false }); // Create a vector source for the markers
      objectsArray.forEach((object) => {
        const customMarkerStyle = this.createCustomMarkerStyle(this.selectedBuildings.some(obj => obj.bd_id === object.bd_id));
        const lonLat = [object.coordinates.long, object.coordinates.lat]; // Order is [longitude, latitude]
        // Convert lon/lat to map coordinates (assuming EPSG:4326 projection)
        const mapCoordinates = transform(lonLat, 'EPSG:4326', 'EPSG:3857');

        // Create a point feature with the custom marker style
        const pointFeature = new Feature({
          geometry: new Point(mapCoordinates),
        });

        pointFeature.set('id', object.bd_id); // Set the 'id' property
        pointFeature.setStyle(customMarkerStyle);

        // Add the feature to the vector source
        vectorSource.addFeature(pointFeature);
      });

      // Create a vector layer and add it to the map
      const vectorLayer = new VectorLayer({
        source: vectorSource,
        zIndex: 9999,
        name: 'buildingsLayer'
      });
      this.map.addLayer(vectorLayer);
    },
    updateMarkerStyles(selectedBuildings) {
      // Get the vector source containing the markers
      const vectorLayer = this.map.getLayers().getArray().find(layer => layer instanceof VectorLayer && layer.get('name') === 'buildingsLayer');
      if(vectorLayer){
        const vectorSource = vectorLayer.getSource();
        // Iterate through the features and update their styles based on selectedBuildings
        vectorSource.getFeatures().forEach(feature => {
          const bdId = feature.get('id');
          const isSelected = selectedBuildings.some(obj => obj.bd_id === bdId);
          const customMarkerStyle = this.createCustomMarkerStyle(isSelected);
          feature.setStyle(customMarkerStyle);
        });
      }
    }
  }
};
</script>

<style scoped>
#dropzone {
  margin-top: 20px;
  min-height: 100px;
}

.management-workspace-map {
  position: relative;
  width: 100%;
}

.theme--light.v-btn.v-btn--disabled:not(.v-btn--icon):not(.v-btn--text):not(.v-btn--outline).aerial {
  background-color: rgba(255, 255, 255, 0.5) !important;
}

.theme--light.v-btn.v-btn--disabled.aerial,
.theme--light.v-btn.v-btn--disabled.aerial .v-icon {
  color: rgb(0 0 0 / 50%) !important;
}

/**
 * checkbox color when not selected
 */
.management-workspace-map .v-input {
  color: white;
}

/**
 * all workspaces button style
 */
.all-workspaces-button {
  z-index: 5;
  position: absolute;
  top: 8px;
}
.npv-class{
  width: 100%;
  height: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/ProjectManagement/Workspaces/WorkspaceMap.vue